"use client";

import { useRef } from "react";
import type { ReactNode } from "react";
import { Button, CheckItem, Eyebrow, SectionShell, useRefGridMode } from "./kit";
import { useEnterStagger } from "./kit/motion";
import { routePath } from "@/lib/routes";
import { REGISTER_URL } from "@/components/pricing/data";

const REASSURE = [
  "No credit card",
  "Free forever tier",
  "GDPR / DSGVO compliant",
  "Export & leave anytime",
];

/** Closing call-to-action. On the homepage grid the card itself is drenched
 *  brand pink, floating on the light grid paper — the dark footer below then
 *  starts on its own clean row edge (owner's pick over the bridge ending; the
 *  unused BridgeBand/`bridge` machinery remains in the kit). Elsewhere it
 *  stays the light card. Copy/CTAs default to the site-wide close but pages
 *  can pass their own. */
export default function ClosingCtaSection({
  eyebrow,
  title = "Ready to build smarter connections?",
  lead = "Branded links, visual routing and LLM-ready responses — start free, no credit card needed.",
  primaryCta = { label: "Start for free", href: REGISTER_URL },
  secondaryCta = { label: "Read the docs →", href: routePath("docs") },
  reassure = REASSURE,
  subline,
}: {
  /** Optional Eyebrow pill above the headline (pink tone on the grid card). */
  eyebrow?: string;
  title?: string;
  lead?: string;
  primaryCta?: { label: string; href: string };
  secondaryCta?: { label: string; href: string };
  /** Reassurance check items under the buttons; pass `[]` to suppress. */
  reassure?: string[];
  /** Optional mono footer line under everything (e.g. a sales side-channel). */
  subline?: ReactNode;
}) {
  const onGrid = useRefGridMode();
  const rootRef = useRef<HTMLDivElement>(null);

  // The card lifts in, then headline → lead → buttons → reassurances cascade.
  useEnterStagger(rootRef, "[data-cta-card]", { y: 40, scale: 0.985, duration: 0.9 });
  useEnterStagger(rootRef, "[data-cta-item]", { y: 22, duration: 0.7, stagger: 0.1, delay: 0.15 });

  const cardSurface = onGrid
    ? "border-white/25 [background:radial-gradient(ellipse_75%_120%_at_50%_0%,rgba(255,255,255,0.18),transparent_55%),linear-gradient(155deg,#fa437f,#d70e62_55%,#b50551)] shadow-[0_30px_80px_-20px_rgba(215,14,98,0.5)]"
    : "border-brand-600/30 [background:radial-gradient(ellipse_80%_130%_at_50%_0%,rgba(215,14,98,0.14),transparent_60%),var(--color-surface)] shadow-ref-lg";

  return (
    <SectionShell divider>
      <div ref={rootRef}>
        {/* data-deco-avoid keeps the decoration field out of the card itself
            while still allowing sparkles in the section's margins. */}
        <div
          data-cta-card
          data-deco-avoid
          // On the grid the card is an opaque drenched surface — the navbar
          // flips to its dark theme while it slides underneath.
          data-nav-surface={onGrid ? "pink" : undefined}
          className={`relative overflow-hidden rounded-[26px] border px-10 py-[74px] text-center ${cardSurface}`}
        >
          <div className="relative z-[1]">
            {eyebrow && (
              <div data-cta-item className="mb-5 flex justify-center">
                <Eyebrow tone={onGrid ? "pink" : "default"}>{eyebrow}</Eyebrow>
              </div>
            )}
            <h2
              data-cta-item
              className={`mb-4 text-[clamp(32px,4.6vw,54px)] font-bold leading-[1.04] tracking-[-0.03em] ${
                onGrid ? "text-white" : ""
              }`}
            >
              {title}
            </h2>
            <p
              data-cta-item
              className={`mx-auto mb-[30px] max-w-[48ch] text-lg ${
                onGrid ? "text-white/85" : "text-muted"
              }`}
            >
              {lead}
            </p>
            <div data-cta-item className="flex flex-wrap justify-center gap-3.5">
              <Button
                href={primaryCta.href}
                variant={onGrid ? "inverse" : "primary"}
                className="px-[26px] py-3.5 text-base"
              >
                {primaryCta.label}
              </Button>
              <Button
                href={secondaryCta.href}
                variant={onGrid ? "ghost-light" : "ghost"}
                className="px-[26px] py-3.5 text-base"
              >
                {secondaryCta.label}
              </Button>
            </div>
            {reassure.length > 0 && (
              <div data-cta-item className="mt-6 flex flex-wrap justify-center gap-[22px] text-[13.5px]">
                {reassure.map((item) => (
                  <CheckItem key={item} tone={onGrid ? "light" : "default"}>
                    {item}
                  </CheckItem>
                ))}
              </div>
            )}
            {subline && (
              <p
                data-cta-item
                className={`mt-[22px] font-jetbrains text-[11.5px] ${
                  onGrid ? "text-white/75" : "text-muted"
                }`}
              >
                {subline}
              </p>
            )}
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
